import 'react-datepicker/dist/react-datepicker.css';

import { Box, Button, Flex, FormControl, FormErrorMessage, FormLabel } from '@chakra-ui/react';
import React, { forwardRef, MouseEventHandler, ReactNode, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import { FiCalendar } from 'react-icons/fi';
import { GoChevronDown } from 'react-icons/go';
import { useSelector } from 'react-redux';

import {
  PlanToolbarChevronDownIcon,
  CalenderIcon,
} from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/icons';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';

import datePickerStyle from './style';

interface Properties {
  value?: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  planPermission?: boolean;
  styles?: React.CSSProperties;
  variant?: 'legacy' | 'revamp';
}

export const DatePickerInput = forwardRef<HTMLButtonElement, Properties>(
  ({ value, onClick, planPermission, styles, variant = 'legacy' }, reference) => {
    const isRevamp = variant === 'revamp';
    const themeColors = useSelector(getPlanPageThemeColors);

    const ChevronIcon: React.ReactNode = useMemo(
      () =>
        isRevamp ? (
          <PlanToolbarChevronDownIcon width={10} height={6} color={themeColors.primary} />
        ) : (
          <GoChevronDown size={18} />
        ),
      [isRevamp, themeColors.primary],
    );

    const CalendarIcon: React.ReactNode = useMemo(
      () =>
        isRevamp ? (
          <CalenderIcon width={14} height={14} color={themeColors.primary} />
        ) : (
          <FiCalendar size={16} color={themeColors.primary} />
        ),
      [isRevamp, themeColors.primary],
    );

    return (
      <Button
        ref={reference}
        cursor={planPermission ? 'not-allowed' : 'pointer'}
        onClick={planPermission ? undefined : onClick}
        leftIcon={CalendarIcon as React.ReactElement}
        rightIcon={ChevronIcon as React.ReactElement}
        width={styles?.width || (isRevamp ? '100%' : 120)}
        height={isRevamp ? '40px' : '30px'}
        variant='picker'
        color={isRevamp && !value ? '#BEBEBE' : isRevamp ? '#121826' : 'white.900'}
        bg={isRevamp ? 'white' : undefined}
        borderRadius={isRevamp ? '10px' : '5px'}
        border={isRevamp ? '1px solid #E8E9EB' : '1px solid #8B89B1'}
        fontSize={styles?.fontSize || (isRevamp ? '14px' : '12px')}
        fontWeight={isRevamp ? 700 : undefined}
        fontFamily={isRevamp ? 'bold' : undefined}
        justifyContent={isRevamp ? 'flex-start' : undefined}
        px={isRevamp ? 3 : undefined}
        _hover={isRevamp ? { bg: 'white' } : undefined}
        style={styles}
      >
        {value || (isRevamp ? 'Select date' : value)}
      </Button>
    );
  },
);

const DatePickerCustom = ({
  selected,
  onChange,
  placeholder,
  label,
  errorMessage,
  timescale,
  onSelect,
  clearDateInput,
  dateId,
  startIn,
  endIn,
  deleteStart,
  clearSection = true,
  isDisabled,
  variant = 'legacy',
}: any) => {
  return (
    <FormControl sx={datePickerStyle} data-testid='datePicker-test'>
      {label && <FormLabel>{label}</FormLabel>}

      <DatePicker
        data-testid='datePicker-test'
        selected={selected}
        onChange={onChange}
        disabled={isDisabled}
        placeholderText={placeholder}
        dateFormat={timescale === 'Y' ? 'yyyy' : 'MMM yyyy'}
        showMonthYearPicker={timescale === 'M'}
        showYearPicker={timescale === 'Y'}
        customInput={<DatePickerInput planPermission={isDisabled} variant={variant} />}
        onSelect={onSelect}
      >
        {clearSection && (
          <Flex
            marginBottom='15px'
            borderTop='1px solid rgba(112, 138, 138, 0.3)'
            width='90%'
            display='flex'
            justifyContent='center'
            alignItems='center'
            paddingTop='10px'
            color='purple'
            onClick={() => {
              clearDateInput(dateId, deleteStart, startIn, endIn);
            }}
          >
            <Box>Clear Selection</Box>
          </Flex>
        )}
      </DatePicker>

      <FormErrorMessage>{errorMessage}</FormErrorMessage>
    </FormControl>
  );
};

export default DatePickerCustom;
